import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import {
  FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows, uniqueSorted,
} from '../../components/SuperAdmin/ListTools';
import { formatExportDate } from '../../utils/exportData';
import {
  HiOutlineLockOpen,
  HiOutlineRefresh,
  HiOutlineTicket,
  HiOutlineUser,
} from 'react-icons/hi';
import { ReportGuide, periodLabel } from '../../components/SuperAdmin/ReportGuide';

const formatDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const PERIODS = [7, 30, 90, 180, 365];

const PRIORITY_LABELS = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};

const formatHours = (h) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h}h`;
  return `${Math.round((h / 24) * 10) / 10}d`;
};

const ReopenedTicketReport = () => {
  const [days, setDays] = useState(30);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['reopened-tickets', days],
    queryFn: () => superAdminAPI.getReopenedTickets({ days }),
    refetchInterval: 120000,
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const byPriority = body.byPriority || {};
  const allRows = Array.isArray(body.rows) ? body.rows : [];
  const [filters, setFilters] = useState({ ...EMPTY_REPORT_FILTERS, reopens: '' });
  const rows = filterReportRows(allRows, filters, {
    searchOf: (r) => [r.ticketNumber, r.subject, r.organizationName, r.reopenReason, r.reopenedByName],
  }).filter((r) =>
    !filters.reopens || (filters.reopens === 'multiple' ? r.reopenCount >= 2 : r.reopenCount === 1));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineLockOpen className="w-7 h-7 text-primary-500" />
            Reopened Tickets
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Tickets the customer opened again after we marked them solved
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-gray-500">Show last</span>
          <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setDays(p)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === p ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {periodLabel(p)}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                       hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ReportGuide
        id="reopened-tickets"
        summary="A reopened ticket usually means the problem was not really fixed. Fewer reopens is better."
        points={[
          ["Reopened tickets", "Tickets opened again at least once."],
          ["Reopen events", "Total number of times tickets were reopened."],
          ["Repeat reopens", "Tickets reopened 2 or more times: check these."],
          ["Avg time to reopen", "Time between marking solved and reopening."],
        ]}
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Reopened Tickets</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalReopened ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Last {body.windowDays ?? days} days</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Reopen Events</p>
          <p className="text-3xl font-bold text-amber-500 mt-2">{stats.totalReopenEvents ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Times a solved ticket was opened again</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Repeat Reopens</p>
          <p className={`text-3xl font-bold mt-2 ${(stats.repeatReopened ?? 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
            {stats.repeatReopened ?? 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">Tickets reopened 2+ times</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg Time to Reopen</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{formatHours(stats.avgHoursToReopen)}</p>
          <p className="text-xs text-gray-400 mt-1">From resolution to reopen</p>
        </div>
      </div>

      {/* By priority chips */}
      {Object.keys(byPriority).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Reopened by Priority</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(byPriority).map(([key, count]) => (
              <span
                key={key}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[key] || 'bg-gray-100 text-gray-600'}`}
              >
                {PRIORITY_LABELS[key] || key}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Reopened tickets table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Reopened Tickets</h2>
          <span className="text-xs text-gray-400">
            {rows.length === allRows.length ? `${rows.length} shown` : `${rows.length} of ${allRows.length} shown`}
          </span>
        </div>

        {allRows.length > 0 && (
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            searchPlaceholder="Search ticket #, subject, organization, reason…"
            organizations={uniqueSorted(allRows.map((r) => r.organizationName))}
            extraActive={!!filters.reopens}
            onResetExtra={() => setFilters((f) => ({ ...f, reopens: '' }))}
            exportProps={{
              fileName: `reopened-tickets-${days}d`,
              getRows: () => rows,
              columns: [
                { header: 'Ticket #', value: (r) => r.ticketNumber },
                { header: 'Subject', value: (r) => r.subject },
                { header: 'Organization', value: (r) => r.organizationName },
                { header: 'Priority', value: (r) => PRIORITY_LABELS[r.priority] || r.priority },
                { header: 'Reopens', value: (r) => r.reopenCount },
                { header: 'Reopen Reason', value: (r) => r.reopenReason },
                { header: 'Current Stage', value: (r) => r.currentStageName },
                { header: 'Reopened By', value: (r) => r.reopenedByName },
                { header: 'Resolved On', value: (r) => formatExportDate(r.resolvedAt) },
                { header: 'Last Reopen', value: (r) => formatExportDate(r.lastReopenedAt) },
              ],
            }}
          >
            <FilterSelect
              value={filters.reopens}
              onChange={(v) => setFilters((f) => ({ ...f, reopens: v }))}
              allLabel="Any Reopen Count"
              options={[['once', 'Reopened once'], ['multiple', 'Reopened 2+ times']]}
            />
          </ReportFilterBar>
        )}

        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading reopened ticket report…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allRows.length > 0 ? 'No tickets match these filters' : 'No tickets were reopened in this period'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Ticket</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Reopens</th>
                  <th className="px-4 py-3">Reopen Reason</th>
                  <th className="px-4 py-3">Reopened By</th>
                  <th className="px-4 py-3">Resolved On</th>
                  <th className="px-4 py-3">Last Reopen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.ticketId || row.ticketNumber} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <HiOutlineTicket className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-gray-900">{row.ticketNumber}</p>
                          <p className="text-xs text-gray-400 max-w-[220px] truncate" title={row.subject}>
                            {row.subject}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.organizationName || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[row.priority] || 'bg-gray-100 text-gray-600'}`}>
                        {PRIORITY_LABELS[row.priority] || row.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                        row.reopenCount >= 2 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {row.reopenCount}×
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {row.reopenReason ? (
                        <p className="text-xs text-gray-600 max-w-[220px] truncate" title={row.reopenReason}>
                          {row.reopenReason}
                        </p>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                      {row.currentStageName && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Now in: {row.currentStageName}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {row.reopenedByName ? (
                        <span className="inline-flex items-center gap-1.5 text-gray-600 text-xs">
                          <HiOutlineUser className="w-3.5 h-3.5 text-gray-400" />
                          {row.reopenedByName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{formatDateTime(row.resolvedAt)}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{formatDateTime(row.lastReopenedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReopenedTicketReport;